import {
	CreateChartWidgetForDashboardParams,
	BuildDashboardContext,
} from './types';
import {
	addWidgetsToExistingDashboard,
	createEmptyDashboard,
	resetWidgetForDashboard,
} from '@/services/dashboards/dashboards-service';
import { buildChartEngine } from '../build-chart/build-chart-engine';
import {
	BuildChartContext,
	ExtendedChartWidgetProps,
} from '../build-chart/types';
import { WidgetProps } from '@sisense/sdk-ui';
import { devDebug } from '@/utils/dev-debug';

export const createChartWidgetForDashboard = async (
	{ dashboardId, userPrompt }: CreateChartWidgetForDashboardParams,
	context: BuildDashboardContext,
) => {
	devDebug('toolCall', {
		tool: 'createChartWidgetForDashboard',
		dashboardId,
		userPrompt,
	});

	const {
		toolCallId,
		dataSourceTitle,
		retrieveDashboard,
		saveDashboard,
		httpClient,
		openAIClient,
		isNlqV3Enabled,
	} = context;
	if (!toolCallId) {
		throw new Error('Error: Invalid tool call ID. Please try again.');
	}

	let isNewDashboard = false;
	let currentDashboard;
	try {
		currentDashboard = retrieveDashboard?.(dashboardId);
		if (!currentDashboard) {
			throw new Error('Dashboard not found. Creating a new dashboard.');
		}
	} catch {
		currentDashboard = await createEmptyDashboard(
			dataSourceTitle,
			`Dashboard for ${dataSourceTitle}`,
			httpClient,
		);
		isNewDashboard = true;
	}

	// reuse build chart engine to create a new chart widget
	let newChartWidgetProps: ExtendedChartWidgetProps | null = null;
	const buildChartContext: BuildChartContext = {
		toolCallId,
		dataSourceTitle,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		retrieveChart: (_: string) => newChartWidgetProps,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		saveChart: (_: string, chartWidgetProps: ExtendedChartWidgetProps) => {
			newChartWidgetProps = chartWidgetProps;
		},
		isNlqV3Enabled,
		httpClient,
		openAIClient,
	};

	const chartSummary = await buildChartEngine(
		{ dataSourceTitle, userPrompt },
		buildChartContext,
	);

	if (chartSummary.isError) {
		throw new Error(chartSummary.message);
	}

	if (!newChartWidgetProps) {
		throw new Error('Error: Failed to create chart widget');
	}

	const resetChartWidgetProps: WidgetProps =
		resetWidgetForDashboard(newChartWidgetProps);

	const updatedDashboardProps = addWidgetsToExistingDashboard(
		currentDashboard,
		[resetChartWidgetProps],
	);

	saveDashboard?.(updatedDashboardProps);

	return {
		message:
			isNewDashboard && newChartWidgetProps
				? 'Dashboard created and chart widget added successfully. Click "Save" to persist changes to My Dashboards.'
				: `Chart widget (widgetId: ${resetChartWidgetProps.id}) added to the dashboard successfully. Click "Save" to persist changes to My Dashboards.`,
	};
};
